import 'prop-types';
import { formatNumber, formatCurrency } from '@atc/string-fns';
import 'react';
import { Text, Box, DisclaimerText, Button } from 'reaxl';
import { calculateDownPayment } from '@atc/wallet-payment-fns';
import { eventBus } from '@atc/cai-event-bus';
import { WALLET_EMAIL_DEALER_REQUEST } from 'wallet-manager';
import clsx from 'clsx';
import PaymentError from '../PaymentError/PaymentError.js';
import PersonalPayment from '../PersonalPayment/PersonalPayment.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
function FinanceTerms(_ref) {
  let {
    rate,
    term,
    termFirst,
    showSpecialAPR,
    isWithRedesign
  } = _ref;
  if (showSpecialAPR) {
    return /*#__PURE__*/jsx(Text, {
      color: "success",
      weight: isWithRedesign ? 'normal' : 'bold',
      className: "display-block",
      children: `Special ${rate}% APR for ${term} mo.`
    });
  }
  if (termFirst) {
    return /*#__PURE__*/jsx(Text, {
      className: "display-block",
      children: `for ${term} mos, ${rate}% APR`
    });
  }
  return /*#__PURE__*/jsx(Text, {
    className: "display-block",
    children: `${rate}% APR for ${term} mo.`
  });
}

/**
 * Renders lease terms in a formatted string showing the lease duration and amount due at signing
 *
 * @component
 * @param {number} dueAtSigning - The amount due at lease signing in dollars
 * @param {number} term - The lease term in months
 * @returns {string} Formatted string containing the lease terms and due at signing amount
 */
const LeaseTerms = _ref2 => {
  let {
    dueAtSigning,
    term
  } = _ref2;
  return `for ${term} months, ${formatCurrency(dueAtSigning)} due at signing`;
};

/**
 * Displays the lender name followed by optional payment details in either an inline or block layout.
 *
 * @component
 * @param {string} lender - The name of the lender to display
 * @param {boolean} inline - When true, lender appears inline with children content. When false, lender appears as block
 * element below children.
 * @param {React.ReactNode} children - Child elements to render after the lender name
 */
function PaymentDetails(_ref3) {
  let {
    lender,
    inline,
    children,
    isWithRedesign
  } = _ref3;
  return /*#__PURE__*/jsxs("div", {
    children: [inline && lender && /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: lender
    }), children, !inline && lender && /*#__PURE__*/jsx(Text, {
      weight: isWithRedesign ? 'normal' : 'bold',
      size: isWithRedesign ? 200 : null,
      className: "display-block",
      children: lender
    })]
  });
}
function FinancingUnavailableDisclaimer(_ref4) {
  let {
    dealType,
    mileage,
    price,
    year
  } = _ref4;
  if (dealType === 'lease') {
    return null;
  }
  const currentYear = new Date().getFullYear();
  const age = currentYear - year;
  if (price > 10000 && age < 10 && mileage < 100000) {
    return null;
  }
  return /*#__PURE__*/jsx(DisclaimerText, {
    className: "margin-top-2 display-block",
    withContrast: true,
    children: "Bank financing may not apply"
  });
}
function Title(_ref5) {
  let {
    type,
    showDisclaimer,
    enableLenderDisclaimerCTA,
    isPennyPerfectPayment,
    isWithRedesign = false,
    isPSX
  } = _ref5;
  const onContactTheDealerCTAClick = () => {
    eventBus.emit(WALLET_EMAIL_DEALER_REQUEST, {
      placement: 'lenderPaymentCTA'
    });
  };
  const renderContactDealerCTA = () => {
    if (enableLenderDisclaimerCTA) {
      return /*#__PURE__*/jsx(Button, {
        bsStyle: "text",
        onClick: onContactTheDealerCTAClick,
        children: "Contact the dealer"
      });
    }
    return /*#__PURE__*/jsx(Text, {
      size: 200,
      children: "Contact the dealer"
    });
  };
  let estimatedPaymentText = 'Estimated Payment';
  if (isPennyPerfectPayment) {
    estimatedPaymentText = 'Monthly Payment';
  } else if (isPSX && isWithRedesign) {
    estimatedPaymentText = 'Estimated Monthly Payment';
  } else if (type && !isWithRedesign) {
    estimatedPaymentText = `Estimated ${type} Payment`;
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(Text, {
      size: 400,
      weight: isWithRedesign ? 'normal' : 'bold',
      componentClass: "div",
      children: estimatedPaymentText
    }), showDisclaimer && /*#__PURE__*/jsxs(DisclaimerText, {
      className: "text-center",
      children: ["Need a lower payment? ", renderContactDealerCTA(), " for more options."]
    })]
  });
}

/**
 *   Displays a formatted lender payment in a bordered box
 *   Includes disclaimers, financing CTAs, and deal toggle
 */
function LenderPayment(_ref6) {
  let {
    appliedOffers,
    callToAction,
    dealType = 'finance',
    disableIncentives = false,
    handleFallbackPayment,
    enableLenderDisclaimerCTA = false,
    interestRate,
    isLeaseEnabled = false,
    isPSX = false,
    lender = 'National Average Rate',
    listingInfo = {},
    paymentsInfo = {},
    personalizedPayment,
    price,
    showLenderName = true,
    showLenderDisclaimer = true,
    showLowPriceDisclaimer = false,
    toggle,
    uiContext,
    vehicleCondition,
    wrapperClassName = '',
    ...rest
  } = _ref6;
  let classNames;
  let borderColor;
  let paymentTextColor;
  let title;
  let content;
  const isError = uiContext === 'error';
  const isWithRedesign = uiContext === 'withRedesign';
  const renderAppliedOffers = function () {
    let className = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'margin-vertical-2';
    if (disableIncentives || !appliedOffers) {
      return null;
    }
    return /*#__PURE__*/jsxs(Text, {
      componentClass: "div",
      color: "success",
      weight: "bold",
      className: className,
      children: [formatCurrency(appliedOffers), " Applied"]
    });
  };
  const renderPaymentDetails = _ref7 => {
    let {
      lenderName,
      inline = false,
      term = null,
      dueAtSigning = null
    } = _ref7;
    return /*#__PURE__*/jsx(PaymentDetails, {
      lender: lenderName,
      inline: inline,
      isWithRedesign: isWithRedesign,
      children: !!term && !!dueAtSigning && /*#__PURE__*/jsx(LeaseTerms, {
        term: term,
        dueAtSigning: dueAtSigning
      })
    });
  };
  const {
    isPennyPerfectPayment,
    loanTerm,
    leaseTerm,
    downPayment,
    downPaymentPercent,
    showSpecialAPR
  } = paymentsInfo || {};
  const downPaymentValue = calculateDownPayment(price, {
    downPayment,
    downPaymentPercent
  });
  switch (uiContext) {
    case 'listing':
      classNames = 'text-center';
      borderColor = 'brand-tertiary';
      paymentTextColor = 'blue-darker';
      break;
    case 'cash':
      classNames = 'text-center margin-bottom-4';
      borderColor = 'neutral-secondary';
      title = /*#__PURE__*/jsx(Title, {
        type: "Cash",
        isWithRedesign: isWithRedesign,
        isPSX: isPSX
      });
      break;
    case 'error':
      classNames = 'text-center margin-bottom-4';
      borderColor = 'neutral-secondary';
      title = /*#__PURE__*/jsx(Title, {
        showDisclaimer: false,
        isWithRedesign: isWithRedesign,
        isPSX: isPSX
      });
      break;
    case 'withRedesign':
    case 'calculator':
    default:
      classNames = 'text-center margin-bottom-4';
      borderColor = 'neutral-secondary';
      title = /*#__PURE__*/jsx(Title, {
        showDisclaimer: showLenderDisclaimer,
        enableLenderDisclaimerCTA: enableLenderDisclaimerCTA,
        isPennyPerfectPayment: isPennyPerfectPayment,
        isWithRedesign: isWithRedesign,
        isPSX: isPSX
      });
      break;
  }
  if (uiContext === 'cash') {
    content = /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsxs(Text, {
        className: "first-price",
        size: 700,
        weight: "ultra-bold",
        children: [formatNumber({
          value: Math.floor(price)
        }), /*#__PURE__*/jsx(Text, {
          componentClass: "sup",
          className: "margin-horizontal-1",
          size: 500,
          weight: "light",
          children: "*"
        })]
      }), renderAppliedOffers(''), !!callToAction && !isError && /*#__PURE__*/jsx("div", {
        className: "margin-top-3 text-size-300",
        children: callToAction
      })]
    });
  } else {
    let personalPaymentUIContext = uiContext;
    if (isError) {
      personalPaymentUIContext = 'error';
    } else if (isWithRedesign) {
      personalPaymentUIContext = 'withRedesign';
    } else {
      personalPaymentUIContext = 'lender';
    }
    content = /*#__PURE__*/jsxs(Fragment, {
      children: [toggle, /*#__PURE__*/jsx(PersonalPayment, {
        textColor: paymentTextColor,
        price: price,
        uiContext: personalPaymentUIContext,
        paymentsInfo: paymentsInfo,
        vehicleCondition: vehicleCondition || 'used',
        personalizedPayment: personalizedPayment,
        handleFallbackPayment: handleFallbackPayment
      }), !isError && (dealType === 'finance' ? /*#__PURE__*/jsxs(Fragment, {
        children: [renderAppliedOffers(), !isWithRedesign && /*#__PURE__*/jsx(Fragment, {
          children: renderPaymentDetails({
            lenderName: showLenderName && lender,
            inline: uiContext === 'calculator'
          })
        }), /*#__PURE__*/jsx(FinanceTerms, {
          term: loanTerm,
          rate: interestRate,
          termFirst: uiContext === 'listing',
          showSpecialAPR: showSpecialAPR,
          isWithRedesign: isWithRedesign
        }), isWithRedesign && /*#__PURE__*/jsx(Fragment, {
          children: renderPaymentDetails({
            lenderName: showLenderName && lender,
            inline: uiContext === 'calculator'
          })
        })]
      }) : /*#__PURE__*/jsxs(Fragment, {
        children: [renderPaymentDetails({
          lenderName: toggle && lender,
          term: leaseTerm,
          dueAtSigning: downPaymentValue
        }), renderAppliedOffers()]
      })), !!callToAction && !isError && /*#__PURE__*/jsx("div", {
        className: "margin-top-3 text-size-200 text-size-sm-300 text-size-md-200 text-size-lg-300",
        children: callToAction
      }), isError && /*#__PURE__*/jsx(PaymentError, {
        className: "margin-top-2"
      }), showLowPriceDisclaimer && /*#__PURE__*/jsx(FinancingUnavailableDisclaimer, {
        dealType: dealType,
        price: price,
        ...listingInfo
      })]
    });
  }
  return /*#__PURE__*/jsxs(Box, {
    "data-cmp": "LenderPayment",
    className: clsx(wrapperClassName, classNames),
    borderColor: borderColor,
    backgroundColor: "transparent",
    ...rest,
    children: [title, content]
  });
}
export { FinanceTerms, FinancingUnavailableDisclaimer, LeaseTerms, PaymentDetails, Title, LenderPayment as default };
